// AuthModal.jsx - WCAG AA Accessible Authentication Modal
import { useState, useEffect, useRef } from "react";
import api from "../services/api";

export default function AuthModal({ onClose, onSuccess }) {
  const [mode, setMode] = useState("login"); // 'login' or 'register'
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const modalRef = useRef(null);
  const previousFocus = useRef(null);

  // Save previous focus and restore on unmount
  useEffect(() => {
    previousFocus.current = document.activeElement;
    return () => {
      if (previousFocus.current) previousFocus.current.focus();
    };
  }, []);

  // Focus trap
  useEffect(() => {
    const element = modalRef.current;
    if (!element) return;

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    // Focus the first input
    const firstInput = element.querySelector("input");
    if (firstInput) firstInput.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const focusableElements = element.querySelectorAll(focusableSelector);
      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    element.addEventListener("keydown", handleKeyDown);
    return () => element.removeEventListener("keydown", handleKeyDown);
  }, [onClose, mode]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (mode === "register") {
        await api.register(form.email, form.password);
      } else {
        await api.login(form.email, form.password);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = {
    width: "100%",
    padding: "10px 12px",
    border: "2px solid #ccc",
    borderRadius: 8,
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  };

  const focusStyle = (e) => {
    e.target.style.borderColor = "#E8622A";
    e.target.style.boxShadow = "0 0 0 3px rgba(232,98,42,0.2)";
  };

  const blurStyle = (e) => {
    e.target.style.borderColor = "#ccc";
    e.target.style.boxShadow = "none";
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      ref={modalRef}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        backdropFilter: "blur(2px)",
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: "32px",
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <h2
            id="auth-modal-title"
            style={{
              margin: 0,
              fontSize: 24,
              fontFamily: "Georgia, serif",
              color: "#1a1a2e",
            }}
          >
            {mode === "login" ? "Sign In" : "Create Account"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close authentication dialog"
            style={{
              background: "none",
              border: "2px solid transparent",
              fontSize: 24,
              cursor: "pointer",
              color: "#666",
              padding: "4px 8px",
              borderRadius: 4,
              lineHeight: 1,
            }}
            onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
            onBlur={(e) => (e.target.style.borderColor = "transparent")}
          >
            ×
          </button>
        </div>

        {/* Error message */}
        <div aria-live="polite" aria-atomic="true">
          {error && (
            <div
              role="alert"
              style={{
                background: "#fef2f2",
                color: "#991b1b",
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 16,
                border: "1px solid #fecaca",
              }}
            >
              {error}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label
              htmlFor="auth-email"
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 6,
                color: "#1a1a2e",
              }}
            >
              Email
            </label>
            <input
              id="auth-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              autoComplete="email"
              aria-required="true"
              style={inputStyles}
              onFocus={focusStyle}
              onBlur={blurStyle}
            />
          </div>


          {/* Password */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="auth-password"
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 6,
                color: "#1a1a2e",
              }}
            >
              Password
            </label>
            <input
              id="auth-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              aria-required="true"
              aria-describedby={mode === "register" ? "password-hint" : undefined}
              style={inputStyles}
              onFocus={focusStyle}
              onBlur={blurStyle}
            />
            {mode === "register" && (
              <p
                id="password-hint"
                style={{ fontSize: 12, color: "#666", marginTop: 4, margin: "4px 0 0" }}
              >
                Minimum 8 characters
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            aria-disabled={loading}
            style={{
              width: "100%",
              padding: "12px",
              background: loading ? "#ccc" : "#E8622A",
              color: "#fff",
              border: "2px solid transparent",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        {/* Toggle mode */}
        <p style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "#444" }}>
          {mode === "login" ? (
            <>
              Don't have an account?{" "}
              <button
                onClick={() => {
                  setMode("register");
                  setError("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#E8622A",
                  cursor: "pointer",
                  fontWeight: 600,
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                  fontSize: 13,
                  padding: 0,
                }}
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                onClick={() => {
                  setMode("login");
                  setError("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#E8622A",
                  cursor: "pointer",
                  fontWeight: 600,
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                  fontSize: 13,
                  padding: 0,
                }}
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
